import { ReactNode } from 'react';

import {
  ConditionalEntityKey,
  ENTITY_DEFINITIONS,
  EntityVisibilityRule,
} from './ConditionalRender.entities';

type EntityProps = Partial<Record<ConditionalEntityKey, EntityVisibilityRule>>;

export interface ConditionalRenderProps extends EntityProps {
  children: ReactNode;
  // When provided, this takes precedence over all other conditions.
  isVisible?: boolean;
}

// Usage examples:
// <ConditionalRender isVisible={false} client={{ showFor: ['umr'] }}>
//   <AlwaysHiddenByOverride />
// </ConditionalRender>
//
// <ConditionalRender client={{ hideFor: [ClientNames.GEHA] }}>
//   <HelpLink />
// </ConditionalRender>
//
// <ConditionalRender lob={{ current: lob, showFor: ['ifp'] }}>
//   <IfpOnlySection />
// </ConditionalRender>
//

const normalize = (value?: string | null) => value?.trim().toLowerCase() || '';

const toNormalizedList = (values?: string[]) =>
  (values || []).map((item) => normalize(item)).filter(Boolean);

export const ConditionalRender = ({
  children,
  isVisible,
  ...entityProps
}: ConditionalRenderProps) => {
  if (typeof isVisible === 'boolean') {
    return isVisible ? <>{children}</> : null;
  }

  const entityKeys = Object.keys(ENTITY_DEFINITIONS) as ConditionalEntityKey[];

  for (const entityKey of entityKeys) {
    const rules = entityProps[entityKey];
    if (!rules) {
      continue;
    }

    const entityDefinition = ENTITY_DEFINITIONS[entityKey];
    const currentValue = rules.current ?? entityDefinition.resolveCurrent?.();
    const normalizedCurrent = normalize(currentValue);
    const hiddenValues = toNormalizedList(rules.hideFor);
    const shownValues = toNormalizedList(rules.showFor);

    const isHidden =
      !!normalizedCurrent && hiddenValues.includes(normalizedCurrent);
    if (isHidden) {
      return null;
    }

    const isAllowed =
      shownValues.length === 0 ||
      (!!normalizedCurrent && shownValues.includes(normalizedCurrent));

    if (!isAllowed) {
      return null;
    }
  }

  return <>{children}</>;
};

export default ConditionalRender;
